/**
 * modePresets — canonical 5-mode preset registry (SSoT).
 *
 * Cycle 101 sprint-3 Task 3.3 (sprint.md:307, SDD §5.2 lines 355-367).
 *
 * Each mode ships a `StoredModeLayoutV1_0` with the full placement list from
 * SDD §5.2 table. `useModeCanvasLayout` (Task 3.4) consumes these when the
 * user has no stored layout OR when `presetVersion` drift triggers
 * cache-bust replay.
 *
 * Module counts per SDD §5.2 (MARITIME re-locked to the canonical Workspace
 * frames in cycle-110 sprint-239 Task 239.2 — see module-verdict-table.md):
 *   WORLD         = 17 (forge + all-source-groups)
 *   GEOPOLITICAL  = 9  (forge + conflict/military/intel/unrest/displacement/cyber/maritime/brief)
 *   FINANCE       = 9  (markets/predictions/vessels/macro/trade/supply/forge/brief/claims)
 *   MARITIME      = 7  (vessels/trade/maritime/supply/aviation/markets/forge)
 *   INVESTIGATIONS = 7 (forge/brief/desk/evidence/claims/informs/paradox)
 *
 * Shared-module invariant: `infra.vessels` appears in both FINANCE and
 * MARITIME presets — same ModuleId, same `VesselTracker` component
 * (SDD §5.2 line 367). Per-instance canvas state (zoom/pan/selection) keys
 * off the placement id, not the ModuleId.
 *
 * Layout conventions:
 *   * 12-col grid, 140px row unit.
 *   * FORGE surfaces claim full-width (span=12) at the top of every mode
 *     where it's pinned per master §Preset contents. Exception: MARITIME
 *     pins FORGE full-width at the BOTTOM per the canonical Workspace 1/2
 *     frames (sprint-239 density/presence pass).
 *   * VesselTracker (where present) takes the top-left 8-col × 4-row slab
 *     per master §Preset contents FINANCE/MARITIME entries.
 *   * Other modules arrange in a 2-col (span=6) standard grid.
 */

import type { ModuleId } from './moduleRegistry';

export type ModeId =
  | 'world'
  | 'geopolitical'
  | 'finance'
  | 'maritime'
  | 'investigations';

/**
 * Bump on every layout-shape change that MUST invalidate user storage.
 * v2 — cycle-110 sprint-239 Task 239.2: MARITIME preset re-locked to the
 * canonical Workspace 1/2 frames (8→7 modules; trade sidebar 4×4; forge
 * moved to bottom; military removed). Stored v1 layouts cache-bust to the
 * recycle tier on next hydration.
 */
export const PRESET_VERSION = 2;

export interface ModulePlacement {
  readonly id: string;
  readonly moduleId: ModuleId;
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
  readonly z?: number;
}

export interface StoredModeLayoutV1_0 {
  readonly mode: ModeId;
  readonly presetVersion: number;
  readonly placements: readonly ModulePlacement[];
  readonly updatedAt: string;
}

// ── Layout helpers ──────────────────────────────────────────────────────

interface LayoutRowSpec {
  readonly modules: readonly {
    readonly moduleId: ModuleId;
    readonly w: number;
    readonly h: number;
  }[];
}

/**
 * Row-shelf placement builder. Each row's modules lay left-to-right from
 * x=0; each row's height = max(h) of its modules. Used by modes that
 * don't need sidebar stacking (WORLD, GEOPOLITICAL, INVESTIGATIONS).
 * FINANCE + MARITIME use explicit placements because their vessel-slab
 * requires sidebar stacking within the slab's y-range (DISS-001-R4).
 */
function buildPlacements(
  mode: ModeId,
  rows: readonly LayoutRowSpec[]
): readonly ModulePlacement[] {
  const placements: ModulePlacement[] = [];
  let y = 0;
  let seq = 0;
  for (const row of rows) {
    let x = 0;
    let rowH = 0;
    for (const m of row.modules) {
      if (x + m.w > 12) {
        throw new Error(
          `preset for '${mode}' overflows: module '${m.moduleId}' at x=${x} w=${m.w}`
        );
      }
      placements.push({
        id: `pl-${mode}-${String(seq).padStart(2, '0')}`,
        moduleId: m.moduleId,
        x,
        y,
        w: m.w,
        h: m.h,
      });
      x += m.w;
      if (m.h > rowH) rowH = m.h;
      seq += 1;
    }
    y += rowH;
  }
  return placements;
}

/**
 * Explicit-placement builder for modes with sidebar-stacked layouts
 * (FINANCE, MARITIME — DISS-001-R4 fix). The caller specifies absolute
 * `{x, y, w, h}` per entry; the helper just assigns deterministic ids
 * and returns the placements in declaration order.
 */
function buildExplicitPlacements(
  mode: ModeId,
  entries: readonly {
    readonly moduleId: ModuleId;
    readonly x: number;
    readonly y: number;
    readonly w: number;
    readonly h: number;
  }[]
): readonly ModulePlacement[] {
  const placements: ModulePlacement[] = [];
  for (let i = 0; i < entries.length; i += 1) {
    const e = entries[i]!;
    if (e.x + e.w > 12) {
      throw new Error(
        `preset for '${mode}' overflows: module '${e.moduleId}' at x=${e.x} w=${e.w}`
      );
    }
    placements.push({
      id: `pl-${mode}-${String(i).padStart(2, '0')}`,
      moduleId: e.moduleId,
      x: e.x,
      y: e.y,
      w: e.w,
      h: e.h,
    });
  }
  return placements;
}

/** Fixed timestamp for preset defaults — presets don't carry session state. */
const PRESET_UPDATED_AT = '2026-04-23T00:00:00.000Z';

// ── WORLD preset (17 modules) ───────────────────────────────────────────
// forge + brief + informs + desk + markets + seismic + maritime + conflicts +
// economic + predictions + trade + supply + climate + cyber + aviation +
// displacement + claims.

const WORLD_PLACEMENTS = buildPlacements('world', [
  // Row 0..2: FORGE full-width
  { modules: [{ moduleId: 'echelon.forge', w: 12, h: 3 }] },
  // Row 3..4: brief + desk
  {
    modules: [
      { moduleId: 'echelon.brief', w: 6, h: 2 },
      { moduleId: 'echelon.desk', w: 6, h: 2 },
    ],
  },
  // Row 5..6: informs + claims
  {
    modules: [
      { moduleId: 'echelon.informs', w: 6, h: 2 },
      { moduleId: 'echelon.claims', w: 6, h: 2 },
    ],
  },
  // Row 7..8: markets + econ.predictions
  {
    modules: [
      { moduleId: 'econ.markets', w: 6, h: 2 },
      { moduleId: 'econ.predictions', w: 6, h: 2 },
    ],
  },
  // Row 9..10: macro + trade
  {
    modules: [
      { moduleId: 'econ.economic', w: 6, h: 2 },
      { moduleId: 'econ.trade', w: 6, h: 2 },
    ],
  },
  // Row 11..12: supply + maritime
  {
    modules: [
      { moduleId: 'econ.supply', w: 6, h: 2 },
      { moduleId: 'infra.maritime', w: 6, h: 2 },
    ],
  },
  // Row 13..14: aviation + seismic
  {
    modules: [
      { moduleId: 'infra.aviation', w: 6, h: 2 },
      { moduleId: 'nat.seismic', w: 6, h: 2 },
    ],
  },
  // Row 15..16: climate + cyber
  {
    modules: [
      { moduleId: 'nat.climate', w: 6, h: 2 },
      { moduleId: 'geo.cyber', w: 6, h: 2 },
    ],
  },
  // Row 17..18: conflicts + displacement
  {
    modules: [
      { moduleId: 'geo.conflicts', w: 6, h: 2 },
      { moduleId: 'geo.displacement', w: 6, h: 2 },
    ],
  },
]);

export const WORLD_PRESET: StoredModeLayoutV1_0 = {
  mode: 'world',
  presetVersion: PRESET_VERSION,
  placements: WORLD_PLACEMENTS,
  updatedAt: PRESET_UPDATED_AT,
};

// ── GEOPOLITICAL preset (9 modules) ─────────────────────────────────────
// forge + conflicts + military + intel + unrest + displacement + cyber +
// maritime + brief.

const GEOPOLITICAL_PLACEMENTS = buildPlacements('geopolitical', [
  { modules: [{ moduleId: 'echelon.forge', w: 12, h: 3 }] },
  {
    modules: [
      { moduleId: 'geo.conflicts', w: 6, h: 2 },
      { moduleId: 'geo.military', w: 6, h: 2 },
    ],
  },
  {
    modules: [
      { moduleId: 'geo.intel', w: 6, h: 2 },
      { moduleId: 'geo.unrest', w: 6, h: 2 },
    ],
  },
  {
    modules: [
      { moduleId: 'geo.displacement', w: 6, h: 2 },
      { moduleId: 'geo.cyber', w: 6, h: 2 },
    ],
  },
  {
    modules: [
      { moduleId: 'infra.maritime', w: 6, h: 2 },
      { moduleId: 'echelon.brief', w: 6, h: 2 },
    ],
  },
]);

export const GEOPOLITICAL_PRESET: StoredModeLayoutV1_0 = {
  mode: 'geopolitical',
  presetVersion: PRESET_VERSION,
  placements: GEOPOLITICAL_PLACEMENTS,
  updatedAt: PRESET_UPDATED_AT,
};

// ── FINANCE preset (9 modules — includes vessels) ──────────────────────
// markets + predictions + vessels + macro + trade + supply + forge + brief +
// claims.

// Explicit sidebar layout: vessels 8×4 top-left; markets + predictions
// stacked 4×2 each in the right sidebar under the vessel-slab y-range
// (y=0..1 and y=2..3). FORGE full-width at y=4. Remaining 2-col grid.
const FINANCE_PLACEMENTS = buildExplicitPlacements('finance', [
  { moduleId: 'infra.vessels', x: 0, y: 0, w: 8, h: 4 },
  { moduleId: 'econ.markets', x: 8, y: 0, w: 4, h: 2 },
  { moduleId: 'econ.predictions', x: 8, y: 2, w: 4, h: 2 },
  { moduleId: 'echelon.forge', x: 0, y: 4, w: 12, h: 3 },
  { moduleId: 'econ.economic', x: 0, y: 7, w: 6, h: 2 },
  { moduleId: 'econ.trade', x: 6, y: 7, w: 6, h: 2 },
  { moduleId: 'econ.supply', x: 0, y: 9, w: 6, h: 2 },
  { moduleId: 'echelon.brief', x: 6, y: 9, w: 6, h: 2 },
  { moduleId: 'echelon.claims', x: 0, y: 11, w: 6, h: 2 },
]);

export const FINANCE_PRESET: StoredModeLayoutV1_0 = {
  mode: 'finance',
  presetVersion: PRESET_VERSION,
  placements: FINANCE_PLACEMENTS,
  updatedAt: PRESET_UPDATED_AT,
};

// ── MARITIME preset (7 modules — includes vessels, shared with FINANCE) ─
// vessels + trade + maritime + supply + aviation + markets + forge.

// Frame-locked layout (cycle-110 sprint-239 Task 239.2, canonical
// Workspace 1/2 frames — MARITIME mode selected in both):
//   * vessels 8×4 top-left slab; trade 4×4 right sidebar (full slab height,
//     "Bilateral Flow Shifts — tariff-sensitive" tile in frame).
//   * Row 2: maritime AIS (6×2) + supply chain (6×2).
//   * Row 3: aviation NOTAMs (6×2) + markets cross-asset (6×2).
//   * FORGE pattern-proposal lane full-width at the BOTTOM, compact (h=2).
//   * geo.military is NOT in the frames' maritime canvas — removed from the
//     preset (still reachable via AddModulePicker + GEOPOLITICAL preset).
const MARITIME_PLACEMENTS = buildExplicitPlacements('maritime', [
  { moduleId: 'infra.vessels', x: 0, y: 0, w: 8, h: 4 },
  { moduleId: 'econ.trade', x: 8, y: 0, w: 4, h: 4 },
  { moduleId: 'infra.maritime', x: 0, y: 4, w: 6, h: 2 },
  { moduleId: 'econ.supply', x: 6, y: 4, w: 6, h: 2 },
  { moduleId: 'infra.aviation', x: 0, y: 6, w: 6, h: 2 },
  { moduleId: 'econ.markets', x: 6, y: 6, w: 6, h: 2 },
  { moduleId: 'echelon.forge', x: 0, y: 8, w: 12, h: 2 },
]);

export const MARITIME_PRESET: StoredModeLayoutV1_0 = {
  mode: 'maritime',
  presetVersion: PRESET_VERSION,
  placements: MARITIME_PLACEMENTS,
  updatedAt: PRESET_UPDATED_AT,
};

// ── INVESTIGATIONS preset (7 modules — no vessels) ─────────────────────
// forge + brief + desk + evidence + claims + informs + paradox.

const INVESTIGATIONS_PLACEMENTS = buildPlacements('investigations', [
  { modules: [{ moduleId: 'echelon.forge', w: 12, h: 3 }] },
  {
    modules: [
      { moduleId: 'echelon.brief', w: 6, h: 2 },
      { moduleId: 'echelon.desk', w: 6, h: 3 },
    ],
  },
  { modules: [{ moduleId: 'echelon.evidence', w: 6, h: 3 }] },
  {
    modules: [
      { moduleId: 'echelon.claims', w: 6, h: 2 },
      { moduleId: 'echelon.informs', w: 6, h: 2 },
    ],
  },
  { modules: [{ moduleId: 'echelon.paradox', w: 6, h: 2 }] },
]);

export const INVESTIGATIONS_PRESET: StoredModeLayoutV1_0 = {
  mode: 'investigations',
  presetVersion: PRESET_VERSION,
  placements: INVESTIGATIONS_PLACEMENTS,
  updatedAt: PRESET_UPDATED_AT,
};

// ── Exhaustive registry ─────────────────────────────────────────────────

export const MODE_PRESETS: Record<ModeId, StoredModeLayoutV1_0> = {
  world: WORLD_PRESET,
  geopolitical: GEOPOLITICAL_PRESET,
  finance: FINANCE_PRESET,
  maritime: MARITIME_PRESET,
  investigations: INVESTIGATIONS_PRESET,
} as const;

export const MODE_IDS: readonly ModeId[] = [
  'world',
  'geopolitical',
  'finance',
  'maritime',
  'investigations',
];

/** Get the preset layout for a mode. Returns a frozen fresh clone so mutation
 *  by consumers cannot corrupt the SSoT. */
export function getPresetForMode(mode: ModeId): StoredModeLayoutV1_0 {
  const preset = MODE_PRESETS[mode];
  return {
    mode: preset.mode,
    presetVersion: preset.presetVersion,
    placements: preset.placements.map((p) => ({ ...p })),
    updatedAt: preset.updatedAt,
  };
}
